const FORMAT: Intl.DateTimeFormatOptions = {
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short"
}

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i

/**
 * An instant from the API. The detail and list endpoints send a naive
 * `YYYY-MM-DD HH:MM:SS` that is UTC; `new Date` would read it in the browser's
 * own zone. An ISO string with an offset (the compose response) is read as is.
 */
export const parseUtcInstant = (value: string): Date | null => {
  const text = value.trim()
  const instant = new Date(
    HAS_OFFSET.test(text) ? text : `${text.replace(" ", "T")}Z`
  )
  return Number.isNaN(instant.getTime()) ? null : instant
}

/** A held message's release time, read in `zone` with that zone's own abbreviation; null when unreadable. */
export const formatInZone = (
  iso: string | null | undefined,
  zone: string | null | undefined,
  locale: string
): string | null => {
  if (!iso || !zone) return null
  const instant = parseUtcInstant(iso)
  if (!instant) return null
  try {
    return new Intl.DateTimeFormat(locale, {
      ...FORMAT,
      timeZone: zone
    }).format(instant)
  } catch {
    return null
  }
}

/** Whether two zones show the same wall clock at `iso`, so a second reading would only repeat the first. */
export const sameClock = (
  iso: string,
  zone: string,
  other: string,
  locale: string
) => formatInZone(iso, zone, locale) === formatInZone(iso, other, locale)

/** Where the zone a hold is read in came from: the recipient's state, the company's zone, or every US zone. */
export type HoldZoneSource = "recipient" | "company" | "us_zones"

/** The hold fields the compose response, the message detail and the send-state forecast all carry. */
export interface HoldInfo {
  hold_reason?: string
  not_before?: string | null
  hold_zone?: string | null
  hold_zone_source?: string | null
  exempt?: string
}

/** A forecast of what a message sent now would do, from `send-state`'s `send_time`. */
export interface SendTimeForecast extends HoldInfo {
  held: boolean
}

export const holdZoneSource = (hold: HoldInfo): HoldZoneSource =>
  hold.hold_zone_source === "company" || hold.hold_zone_source === "us_zones"
    ? hold.hold_zone_source
    : "recipient"

/** The forecast for `channel` and `category` in a recipient's send-state, when the server sent one. */
export const sendTimeOf = (
  check: unknown,
  channel: "email" | "sms",
  category: "transactional" | "marketing" | undefined
): SendTimeForecast | undefined => {
  if (!check || typeof check !== "object" || !category) return undefined
  const channels = (check as { channels?: Record<string, unknown> }).channels
  const state = channels?.[channel] as
    | { send_time?: Partial<Record<string, SendTimeForecast>> }
    | undefined
  return state?.send_time?.[category]
}
